import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get, trim, isEmpty } from 'lodash';
import { withRouter } from 'react-router';
import { compose, setDisplayName } from 'recompose';

import { Divider, Text } from 'base-components';
import { px2rem } from 'decisiv-ui-utils';
import { Container, Row, Column } from 'styled-components-reactive-grid';

import TextGhostIndicator from 'elements/TextGhostIndicator';

import { bffAPILink } from 'utils/bff';

import { CSRF_TOKEN } from '../../../constants';

import AssignUserAction from './AssignUserAction';
import CaseAssignedToAvatar from './CaseAssignedToAvatar';
import withCaseAssignUser from './withCaseAssignUser';
import { unassignCaseMutation } from './withCaseAssignUser/withUnassignCase';

const FIXED_HEIGHT_PX = 35;

class CaseAssignUserButton extends Component {
  componentDidMount() {
    window.addEventListener('beforeunload', this.handleBeforeUnloadEvent);

    this.tryToAssignCaseToCurrentUser();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // caseId may not exist on mount
    if (
      (!prevProps.caseId || prevProps.caseAssignedToLoading) &&
      (this.props.caseId && !this.props.caseAssignedToLoading)
    ) {
      this.tryToAssignCaseToCurrentUser();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnloadEvent);

    /**
     * This handles the auto-unassign for in-app navigation, such as when navigating from a
     * case page to the search results. When using the normal async request the search results may
     * load with the user still showing as assigned to the case.
     */
    if (this.props.isAssignedToCurrentUser) {
      this.SYNCHRONOUS_unassignCase();
    }
  }

  tryToAssignCaseToCurrentUser = () => {
    if (this.props.isReadOnly) {
      return;
    }

    // Passing `true` sets the `onlyIfUnassigned` flag on the mutation. This
    // prevents re-assigning the case if it is currently assigned to someone
    // else - and also prevents the need to query for and check the current
    // case assignment before assigning it to the current user.
    if (this.props.caseId) {
      this.props.assignCaseToCurrentUser(true);
    }
  };

  /**
   * Before the DOM is unloaded (such as when a tab or window is closed) the case should be
   * automatically unassigned. Because the unload might happen before a normal async fetch request
   * is complete, we need to send this as a synchronous request instead.
   * A beacon is a common technique for this, but we need custom headers so we must revert
   * to using old skool XHR. Learn more about beacons here:
   * https://developer.mozilla.org/en-US/docs/Web/API/Beacon_API#Why_use_Beacon
   * @memberof CaseAssignUserButton
   */
  handleBeforeUnloadEvent = () => {
    this.SYNCHRONOUS_unassignCase();
  };

  /**
   * Issues a synchronous request and blocks the thread until it returns. This specific behavior
   * is required for the auto-unassign functionality during navigation. Atypical naming convention
   * used to help ensure you are totally aware of what you are using.
   * @memberof CaseAssignUserButton
   */
  // eslint-disable-next-line camelcase
  SYNCHRONOUS_unassignCase = () => {
    if (this.props.isReadOnly) {
      return;
    }

    try {
      const body = {
        operationName: get(unassignCaseMutation, 'definitions.0.name.value'),
        variables: { caseId: this.props.caseId },
        query: get(unassignCaseMutation, 'loc.source.body'),
      };
      const req = new XMLHttpRequest();
      req.open('POST', bffAPILink, false); // third parameter makes this a synchronous request
      req.setRequestHeader('accept', '*/*');
      req.setRequestHeader('content-type', 'application/json');
      req.setRequestHeader('x-csrf-token', memoryDB.getItem(CSRF_TOKEN));
      req.withCredentials = true; // includes the auth cookies.
      req.send(JSON.stringify(body));

      const json = JSON.parse(req.responseText);

      if (get(json, 'errors.length')) {
        // eslint-disable-next-line no-console
        console.error(
          'Errors while unassigning the case:',
          get(json, 'errors'),
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  navigateToDashboard = () => this.props.history.push('/');

  render() {
    const {
      assignCaseToCurrentUser,
      caseAssignedTo,
      isAssigned,
      isAssignedToCurrentUser,
      caseAssignedToLoading,
      isReadOnly,
    } = this.props;

    const isLoading = this.props.isLoading || caseAssignedToLoading;

    if (!isLoading && isReadOnly && isEmpty(caseAssignedTo)) {
      return null;
    }

    return (
      <Container modifiers={['padScale_0']}>
        <Row>
          <Column modifiers={['padScaleX_4', 'padScaleY_0']}>
            <Divider modifiers="vertical" />
          </Column>
          <Column
            style={{
              alignSelf: 'center',
              paddingRight: px2rem(10),
              minHeight: px2rem(FIXED_HEIGHT_PX),
            }}
            modifiers="padScale_0"
          >
            <CaseAssignedToAvatar
              caseAssignedTo={caseAssignedTo}
              isAssigned={isAssigned}
              isLoading={isLoading}
            />
          </Column>
          <Column
            modifiers="padScale_0"
            style={{ minHeight: px2rem(FIXED_HEIGHT_PX) }}
          >
            {isLoading && (
              <Row modifiers={['height_100', 'middle']}>
                <Column modifiers="padScale_0">
                  <TextGhostIndicator style={{ width: px2rem(80) }}>
                    .
                  </TextGhostIndicator>
                </Column>
              </Row>
            )}
            {!isLoading &&
              isAssigned && (
                <Row>
                  <Column modifiers="padScale_0" style={{ top: '2px' }}>
                    <Text>
                      {`${trim(caseAssignedTo.name) || caseAssignedTo.email}`}
                    </Text>
                  </Column>
                </Row>
              )}
            {!isLoading &&
              !isReadOnly && (
                <AssignUserAction
                  assignCaseToCurrentUser={assignCaseToCurrentUser}
                  isAssigned={isAssigned}
                  isAssignedToCurrentUser={isAssignedToCurrentUser}
                  unassignCase={this.navigateToDashboard}
                />
              )}
          </Column>
        </Row>
      </Container>
    );
  }
}

CaseAssignUserButton.propTypes = {
  assignCaseToCurrentUser: PropTypes.func.isRequired,
  caseAssignedTo: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  caseId: PropTypes.string,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  isAssigned: PropTypes.bool.isRequired,
  isAssignedToCurrentUser: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  caseAssignedToLoading: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
};

CaseAssignUserButton.defaultProps = {
  caseAssignedTo: {},
  caseId: undefined,
  isReadOnly: false,
};

export default compose(
  setDisplayName('CaseAssignUserButton'),
  withCaseAssignUser,
  withRouter,
)(CaseAssignUserButton);



// WEBPACK FOOTER //
// ./src/compositions/CaseDetailOverviewPanel/CaseAssignUserButton/index.js